import { IContact } from '@dataTypes/contact.type'
import { ICampaignChannel, ICampaignTemplate } from '@dataTypes/marketing.type'

/** Keeps template pickers aligned with Send via / campaign channel (`SMS` | `Email`). */
export function filterCampaignTemplatesBySendChannel(
  templates: ICampaignTemplate[],
  sendChannel: string | undefined,
): ICampaignTemplate[] {
  if (!sendChannel) return templates
  return templates.filter((t) => t.channel === sendChannel)
}

/** FE mock ids are not DB UUIDs — persist null, content is already on the campaign. */
export function toPersistedCampaignTemplateId(
  id: string | null | undefined,
): string | null {
  if (!id?.trim()) return null
  if (id.startsWith('mock-')) return null
  return id
}

/** Contact can receive campaign email (has a non-empty address). */
export function contactHasCampaignEmail(contact?: IContact | null): boolean {
  return !!contact?.email?.trim()
}

/** Contact can receive campaign SMS (has a non-empty cellphone). */
export function contactHasCampaignSms(contact?: IContact | null): boolean {
  return !!contact?.cellphone?.trim()
}

/** Filter contacts eligible for the selected campaign Send via channel. */
export function filterContactsByCampaignChannel(
  contacts: IContact[],
  channel: string | undefined,
): IContact[] {
  if (channel === 'Email') {
    return contacts.filter((c) => contactHasCampaignEmail(c))
  }
  if (channel === 'SMS') {
    return contacts.filter((c) => contactHasCampaignSms(c))
  }
  return contacts
}

export const getTemplateNameById = (
  id: string,
  templates: ICampaignTemplate[],
): string => {
  const template = templates.find((a) => a.campaign_template_id === id)

  return template ? template.name : ''
}

export const getUniqueList = <T extends { name: string }>(
  originalList: T[],
): T[] => {
  const recordedName = new Set<string>()
  return originalList.filter((el) => {
    if (recordedName.has(el.name)) return false
    recordedName.add(el.name)
    return true
  })
}

export const calculateTotalInteractionCount = (
  type: 'sent_count' | 'open_count' | 'click_count',
  channels?: (ICampaignChannel | Partial<ICampaignChannel>)[],
): number => {
  let total = 0
  if (!channels || channels.length === 0) return total

  channels.forEach((channel) => {
    total += channel[type] ?? 0
  })
  return total
}
